import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../App";
import apiClient from "../utils/apiClient";
import Layout from "../components/Layout";
import {
  Building2,
  Users,
  Settings,
  BarChart3,
} from "lucide-react";

// Import modular components
import {
  EmptyState,
  StatusBadge,
  WelcomeState,
  MemberWelcomeState,
  ActivationAnalytics,
  MembersTab,
  SettingsTab,
} from "../components/org";

const ROLE_LABELS = { owner: "Owner", admin: "Admin", member: "Member" };

export default function OrgDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [orgData, setOrgData] = useState(null);
  const [members, setMembers] = useState([]);
  const [usage, setUsage] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("members");
  const [memberWelcomeDismissed, setMemberWelcomeDismissed] = useState(false);

  // Read tab from query param
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("tab") === "members") setTab("members");
  }, []);

  useEffect(() => {
    loadOrgData();
  }, []);

  const loadOrgData = async () => {
    try {
      const res = await apiClient.get("/orgs/me", { skipErrorToast: true });
      if (res.data.organization) {
        setOrgData(res.data);
        const orgId = res.data.organization.id;
        const [membersRes, usageRes, statsRes] = await Promise.all([
          apiClient
            .get(`/orgs/${orgId}/members`, { skipErrorToast: true })
            .catch(() => ({ data: { members: [] } })),
          apiClient
            .get(`/orgs/${orgId}/usage`, { skipErrorToast: true })
            .catch(() => ({ data: { questions_used: 0, reviews_used: 0 } })),
          apiClient
            .get(`/orgs/${orgId}/stats`, { skipErrorToast: true })
            .catch(() => ({ data: { agreement_count: 0 } })),
        ]);
        setMembers(membersRes.data.members || []);
        setUsage(usageRes.data);
        setStats(statsRes.data);
      }
    } catch (error) {
      // User has no organization - this is expected for non-org users
      console.debug('No organization found for user:', error?.response?.status || 'unknown');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="max-w-4xl mx-auto px-4 py-16">
          <div className="animate-pulse space-y-4">
            <div className="h-8 bg-muted rounded w-48" />
            <div className="h-40 bg-muted rounded" />
          </div>
        </div>
      </Layout>
    );
  }

  if (!orgData?.organization) {
    return (
      <Layout>
        <div className="max-w-4xl mx-auto px-4 py-16">
          <EmptyState />
        </div>
      </Layout>
    );
  }

  const org = orgData.organization;
  const myRole = orgData.membership?.role;
  const isAdminOrOwner = myRole === "owner" || myRole === "admin";
  const agreementCount = stats?.agreement_count ?? 0;
  const showOwnerWelcome =
    isAdminOrOwner && org.status === "active" && agreementCount === 0;

  // Member welcome: server-side completion flag + localStorage snooze fallback
  const snoozeKey = `member_welcome_snooze_until_${org.id}`;
  const serverCompleted = orgData.membership?.onboarding_completed === true;
  const snoozedUntil = localStorage.getItem(snoozeKey);
  const isSnoozed = snoozedUntil && Date.now() < Number(snoozedUntil);

  const showMemberWelcome =
    !isAdminOrOwner &&
    !serverCompleted &&
    !isSnoozed &&
    !memberWelcomeDismissed;

  const handleOnboardingComplete = async (action) => {
    try {
      await apiClient.post(`/orgs/${org.id}/onboarding/complete`, { action });
    } catch (error) {
      console.error('Failed to complete onboarding:', error);
    }
    setMemberWelcomeDismissed(true);
  };

  const snoozeMemberWelcome = () => {
    // Snooze for 24 hours
    localStorage.setItem(snoozeKey, String(Date.now() + 24 * 60 * 60 * 1000));
    setMemberWelcomeDismissed(true);
  };

  if (showOwnerWelcome) {
    return (
      <Layout>
        <div className="px-4 py-8">
          <WelcomeState
            org={org}
            members={members}
            usage={usage}
            agreementCount={agreementCount}
          />
        </div>
      </Layout>
    );
  }

  if (showMemberWelcome) {
    return (
      <Layout>
        <div className="px-4 py-8">
          <MemberWelcomeState
            org={org}
            myRole={myRole}
            members={members}
            usage={usage}
            stats={stats}
            onComplete={handleOnboardingComplete}
            onSnooze={snoozeMemberWelcome}
          />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-4xl mx-auto px-4 py-8" data-testid="org-dashboard">
        {/* Header */}
        <div className="flex items-start justify-between mb-8">
          <div>
            <div className="flex items-center gap-3 mb-1">
              <Building2 className="w-6 h-6 text-primary" />
              <h1 className="font-heading text-2xl font-semibold">
                {org.name}
              </h1>
            </div>
            <div className="flex items-center gap-3 mt-2">
              <StatusBadge status={org.status} />
              <span className="text-sm text-muted-foreground">
                {ROLE_LABELS[myRole]} · {org.member_count} member
                {org.member_count !== 1 ? "s" : ""}
              </span>
            </div>
          </div>
        </div>

        {/* Usage meters */}
        {usage && org.status === "active" && (
          <div className="grid grid-cols-2 gap-4 mb-8" data-testid="usage-meters">
            <div className="bg-card border border-border rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <BarChart3 className="w-4 h-4 text-primary" />
                <p className="text-xs text-muted-foreground uppercase tracking-wider">
                  Questions This Month
                </p>
              </div>
              <p className="text-2xl font-semibold" data-testid="questions-used">
                {usage.questions_used}
              </p>
            </div>
            <div className="bg-card border border-border rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <BarChart3 className="w-4 h-4 text-primary" />
                <p className="text-xs text-muted-foreground uppercase tracking-wider">
                  Case Reviews This Month
                </p>
              </div>
              <p className="text-2xl font-semibold" data-testid="reviews-used">
                {usage.reviews_used}
              </p>
            </div>
          </div>
        )}

        {/* Activation Analytics (owner/admin only) */}
        {isAdminOrOwner && org.status === "active" && (
          <ActivationAnalytics
            orgId={org.id}
            onInviteClick={() => setTab("members")}
          />
        )}

        {/* Tabs */}
        <div className="border-b border-border mb-6">
          <nav className="flex gap-6">
            {[
              { key: "members", label: "Members", icon: Users },
              { key: "settings", label: "Settings", icon: Settings },
            ].map((t) => (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                className={`flex items-center gap-2 pb-3 text-sm font-medium border-b-2 transition ${
                  tab === t.key
                    ? "border-primary text-primary"
                    : "border-transparent text-muted-foreground hover:text-foreground"
                }`}
                data-testid={`tab-${t.key}`}
              >
                <t.icon className="w-4 h-4" />
                {t.label}
              </button>
            ))}
          </nav>
        </div>

        {tab === "members" && (
          <MembersTab
            orgId={org.id}
            members={members}
            setMembers={setMembers}
            myRole={myRole}
          />
        )}
        {tab === "settings" && <SettingsTab org={org} myRole={myRole} />}
      </div>
    </Layout>
  );
}
